import { buildTicketSearchUrl, buildAdminTicketsUrl } from './utils';

/**
 * Outbound Calling API Factory
 */
export default function outboundCallingApi(fetch, baseUrl) {
  return {
    /**
     * Get agent online status
     */
    async getAgentStatus({ agentId }) {
      return fetch.get(`${baseUrl}/agent/${agentId}/isOnline`);
    },

    /**
     * Update agent online status
     */
    async updateAgentStatus({ agentId, isOnline }) {
      return fetch.patch(`${baseUrl}/agent/${agentId}?isOnline=${isOnline}`);
    },

    /**
     * Get tickets by agent ID with optional search parameters
     * @param {string} agentId - Agent ID (path variable)
     * @param {object} payload - Optional query parameters
     * @param {string} payload.searchKey - Search key (field name)
     * @param {string} payload.searchValue - Search value
     * @param {number} payload.pageNumber - Page number (default: 0)
     * @param {number} payload.pageSize - Page size (default: 10)
     */
    async getTicketsByAgent({ agentId, payload = {} }) {
      const url = buildTicketSearchUrl(`${baseUrl}/tickets/${agentId}`, payload);
      return fetch.get(url);
    },

    /**
     * Update ticket (status, priority, etc.)
     */
    async updateTicket({ ticketId, agentId, updates }) {
      return fetch.patch(`${baseUrl}/tickets/${ticketId}/by/${agentId}`, updates);
    },

    /**
     * Get admin dashboard data
     * Returns agent availability, queue details, and call analytics
     */
    async getAdminDashboardData() {
      return fetch.get(`${baseUrl}/metrics`);
    },

    /**
     * Get available agents list
     * Returns list of all agents with their status and last ping time
     */
    async getAvailableAgents() {
      return fetch.get(`${baseUrl}/available-agents`);
    },

    /**
     * Get tickets queue (open tickets)
     * @param {object} payload - Query parameters
     * @param {string} payload.ticketId - Filter by ticket ID
     * @param {string} payload.agentId - Filter by agent ID
     * @param {string|string[]} payload.status - Filter by status
     * @param {string} payload.sortBy - Sort field
     * @param {string} payload.sortDir - Sort direction (asc/desc)
     * @param {number} payload.page - Page number (0-indexed)
     * @param {number} payload.pageSize - Page size
     */
    async getTicketsQueue(payload = {}) {
      const url = buildAdminTicketsUrl(`${baseUrl}/tickets`, payload);
      return fetch.get(url);
    },

    /**
     * Get closed tickets
     * @param {object} payload - Query parameters
     * @param {string} payload.ticketId - Filter by ticket ID
     * @param {string} payload.agentId - Filter by agent ID
     * @param {string|string[]} payload.status - Filter by status
     * @param {number} payload.lastHours - Filter by last N hours
     * @param {string} payload.sortBy - Sort field
     * @param {string} payload.sortDir - Sort direction (asc/desc)
     * @param {number} payload.page - Page number (0-indexed)
     * @param {number} payload.pageSize - Page size
     */
    async getClosedTickets(payload = {}) {
      const url = buildAdminTicketsUrl(`${baseUrl}/tickets`, payload);
      return fetch.get(url);
    }
  };
}
